import React, { useRef } from "react";
import styled from "styled-components";
import { Container, Eyebrow, Display, Lead, useReveal } from "../landing/ui";
import { colors, fonts, media } from "../../styles/theme";
import { pilotVoices } from "../../pages/verticalContent";

// Two columns of the same people: inductions as they were, then the same material through
// vimbal, one row per person so the pair is the same voice on both sides.

const Wrap = styled.section`
  padding: 0 0 120px;
`;

const Head = styled.div`
  max-width: 760px;
  margin-bottom: 44px;
`;

const Grid = styled.div`
  display: grid;
  gap: 14px;
  align-items: stretch;

  @media ${media.medium} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 28px;
    row-gap: 18px;
  }
`;

const Heads = styled.div`
  display: none;

  @media ${media.medium} {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 28px;
    margin-bottom: 14px;
  }
`;

// On phones the pair stacks, so each card needs its own label to stay legible.
const MobileLabel = styled.span`
  display: block;
  margin-bottom: 8px;
  font-family: ${fonts.mono};
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(p) => (p.$after ? p.$accent : colors.pebble)};

  @media ${media.medium} {
    display: none;
  }
`;

const ColumnHead = styled.h3`
  display: flex;
  align-items: baseline;
  gap: 12px;
  font-family: ${fonts.mono};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${(p) => (p.$after ? p.$accent : colors.pebble)};
  margin-bottom: 4px;

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${(p) => (p.$after ? p.$accent : colors.stone)};
    opacity: 0.5;
  }
`;

const Card = styled.blockquote`
  position: relative;
  height: 100%;
  padding: 22px 24px;
  border-radius: 20px;
  border: 1px solid ${(p) => (p.$after ? "transparent" : colors.stone)};
  background: ${(p) => (p.$after ? colors.linen : "transparent")};
  box-shadow: ${(p) => (p.$after ? "0 1px 2px rgba(42, 35, 30, 0.06)" : "none")};

  p {
    font-family: ${fonts.body};
    font-size: 17px;
    line-height: 1.55;
    color: ${(p) => (p.$after ? colors.ink : colors.bark)};
  }

  cite {
    display: block;
    margin-top: 12px;
    font-family: ${fonts.mono};
    font-size: 11px;
    font-style: normal;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${(p) => (p.$after ? p.$accent : colors.pebble)};
  }
`;


// Sits in the column gap on the row's centre line, so each pair reads left-to-right as one
// person's before and after. Hidden once the pair stacks, the per-card labels take over.
const Turn = styled.span`
  display: none;

  @media ${media.medium} {
    display: grid;
    place-items: center;
    position: absolute;
    top: 50%;
    left: -14px;
    transform: translate(-50%, -50%);
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: ${colors.limestone};
    color: ${(p) => p.$accent};
    font-size: 14px;
    line-height: 1;
    z-index: 1;
  }
`;




const Closing = styled.blockquote`
  margin-top: 48px;
  padding: 32px 28px;
  border-radius: 26px;
  background: ${colors.forest};
  color: ${colors.linen};
  text-align: center;

  p {
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(24px, 2.8vw, 34px);
    line-height: 1.2;
    letter-spacing: -0.02em;
    color: ${colors.copperLight};
    text-wrap: balance;
  }

  cite {
    display: block;
    margin-top: 16px;
    font-family: ${fonts.mono};
    font-size: 11px;
    font-style: normal;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(250, 247, 241, 0.6);
  }

  @media ${media.medium} {
    padding: 48px 56px;
  }
`;

const Note = styled.p`
  margin-top: 18px;
  font-family: ${fonts.body};
  font-size: 14px;
  color: ${colors.pebble};
`;

const PilotVoices = ({ accent = colors.terracotta, limit }) => {
  const scope = useRef(null);
  useReveal(scope);

  return (
    <Wrap ref={scope} aria-labelledby="v-pilot-voices">
      <Container>
        <Head>
          <Eyebrow $color={accent}>{pilotVoices.eyebrow}</Eyebrow>
          <Display id="v-pilot-voices" style={{ margin: "18px 0 20px" }} data-reveal>
            {pilotVoices.titleLead} <em>{pilotVoices.titleEmphasis}</em>
          </Display>
          <Lead data-reveal="0.1">{pilotVoices.intro}</Lead>
        </Head>

        <Heads>
          <ColumnHead>{pilotVoices.beforeTitle}</ColumnHead>
          <ColumnHead $after $accent={accent}>
            {pilotVoices.afterTitle}
          </ColumnHead>
        </Heads>

        <Grid>
          {(limit ? pilotVoices.pairs.slice(0, limit) : pilotVoices.pairs).map((p, i) => (
            <React.Fragment key={p.who + i}>
              <Card data-reveal={(i % 3) * 0.06}>
                <MobileLabel>{pilotVoices.beforeTitle}</MobileLabel>
                <p>{p.before}</p>
                <cite>{p.who}</cite>
              </Card>
              <Card $after $accent={accent} data-reveal={(i % 3) * 0.06}>
                <Turn $accent={accent} aria-hidden="true">
                  →
                </Turn>
                <MobileLabel $after $accent={accent}>
                  {pilotVoices.afterTitle}
                </MobileLabel>
                <p>{p.after}</p>
                <cite>{p.who}</cite>
              </Card>
            </React.Fragment>
          ))}
        </Grid>

        <Closing data-reveal>
          <p>{pilotVoices.closingQuote}</p>
          <cite>{pilotVoices.closingWho}</cite>
        </Closing>
        <Note>{pilotVoices.note}</Note>
      </Container>
    </Wrap>
  );
};

export default PilotVoices;
